/* eslint complexity: ["error", 7] */
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import classNames from 'classnames';
import FlagDropdown from '../FlagDropdown/FlagDropdown';
import PhoneField from '../PhoneField/PhoneField';
import PhoneFieldWithoutMask from '../PhoneField/PhoneFieldWithoutMask';
import FlagData from './phoneNumberData.json';
import './InputTel.less';

/* istanbul ignore next */
const noop = () => {};

class InputTel extends Component {
    static propTypes = {
        /** id attribute for form field */
        id: PropTypes.string.isRequired,
        /** The phone number to populate the input field */
        phoneNumber: PropTypes.string,
        /** The country code of the flag you want displayed initially. */
        countryCode: PropTypes.string,
        /** Boolean that allows consuming app to NOT display the Country Code dropdown. */
        showCountrySelect: PropTypes.bool,
        /** When populated with a string, this will turn the border red and display a validation error beneath the input. */
        errorMsg: PropTypes.string,
        /** When populated with a string, this will display help text beneath the input. */
        helpText: PropTypes.string,
        /** Function that gets called when the user selects a new country from the dropdown. This function should set the value of countryCode in your store. */
        onCountryCodeChange: PropTypes.func,
        /**
         * Function that gets called every time the user types in the input field.
         *  This function should set the value of phoneNumber in your store. This
         *  callback provides the event object and a phoneObject that includes
         *  {raw: (string) unformatted phone number, formatted: (string) formatted phone number,
         *  phoneCode: (string) phone code, isValid: (boolean) whether the phone number is valid}.
         */
        onPhoneChange: PropTypes.func,
        /**
         * Function called when the input field gets focus. Callback signature:
         *  `function(event, phoneObject)` where phoneObject provides the current
         *  state of the phone field and has the same shape as the phoneObject
         *  received in the onPhoneChange handler.
         */
        onPhoneFocus: PropTypes.func,
        /**
         * Function called when the input field loses focus. Callback signature:
         *  `function(event, phoneObject)` where phoneObject provides the current
         *  state of the phone field and has the same shape as the phoneObject
         *  received in the onPhoneChange handler.
         */
        onPhoneBlur: PropTypes.func,
        /**
         * Function called when an error occurs displaying the specified data.
         * Currently, this is only invoked if the specified `countryCode` is not found.
         */
        onError: PropTypes.func,
        /** All translations. */
        l10n: PropTypes.PropTypes.shape({
            'reactInputTel.input.countryCode': PropTypes.string,
            'reactInputTel.input.label': PropTypes.string,
        }),
        /** Translations for the countries with the key as the country code translation key and the value as the country name. */
        countryCodes: PropTypes.objectOf(PropTypes.string),
        /** Determines if a defined format mask should be applied to the phone number digits. (For A/B test use only) */
        useMask: PropTypes.bool,
        /**  An array of allowed country codes to be listed when we only want to show specific countries instead of the whole list */
        allowedCountries: PropTypes.arrayOf(PropTypes.string),
        /** ARIA: Indicates if the field is required */
        'aria-required': PropTypes.string,
    };

    static defaultProps = {
        id: 'react-input-tel',
        phoneNumber: '',
        countryCode: 'us',
        countryCodes: {},
        showCountrySelect: true,
        errorMsg: '',
        helpText: '',
        onCountryCodeChange: noop,
        onPhoneChange: noop,
        onPhoneFocus: noop,
        onPhoneBlur: noop,
        onError: noop,
        l10n: {
            'reactInputTel.input.countryCode': 'Country Code',
            'reactInputTel.input.label': 'Phone Number',
        },
        useMask: true,
    };

    constructor(props) {
        super(props);
        // create a shallow copy of each FlagData object for each instance of the input tel component
        this.flagData = Object.keys(FlagData).reduce((data, key) => {
            const countryList = props.allowedCountries;
            const {...obj} = countryList
                ? countryList.includes(key) && FlagData[key]
                : FlagData[key];
            if (Object.keys(obj).length !== 0) {
                data[key] = obj;
            }
            return data;
        }, {});

        this.mergeFlagData();

        const countryData = this.getCountryData(props.countryCode);

        this.state = {
            phoneCode: countryData ? countryData.phoneCode : '1',
            phoneMasks: countryData ? countryData.masks : [],
        };
    }

    componentDidUpdate(prevProps, prevState) {
        const didCountryCodePropsChange = this.compareCountryCodeProps(
            this.props
        );
        const countryData = this.getCountryData(this.props.countryCode);
        const phoneCode = countryData ? countryData.phoneCode : '1';

        if (didCountryCodePropsChange) {
            this.mergeFlagData();
        }

        if (phoneCode !== prevState.phoneCode) {
            this.setState({
                phoneCode: countryData ? countryData.phoneCode : '1',
                phoneMasks: countryData ? countryData.masks : [],
            });
        }
    }

    // check to see if any of the country code translations have changed. If so, will have to re-merge this.flagData
    compareCountryCodeProps(nextProps) {
        return (
            nextProps &&
            nextProps.countryCodes &&
            Object.keys(this.flagData).some((code) => {
                // This fallback is there for react-site-footer. Remove when
                // that component is updated.
                const nextCountryName =
                    nextProps.countryCodes[`reactInputTel.countries.${code}`] ||
                    nextProps.countryCodes[code];
                const currentCountry = this.flagData[code];

                return (
                    nextCountryName &&
                    currentCountry &&
                    nextCountryName !== currentCountry.name
                );
            })
        );
    }

    getCountryData(countryCode) {
        const countryData = this.flagData[countryCode];
        if (!countryCode || !countryData) {
            this.props.onError(new Error('Invalid country code'));
            return this.flagData.default;
        }
        return countryData;
    }

    handleCountryCodeChange = (e) => {
        if (!e || !e.target || !e.target.value) {
            return;
        }
        // React pools events and reuses the object so save the value in case
        // it gets overwritten.
        const cc = e.target.value;
        const countryData = this.flagData[cc];
        if (countryData) {
            countryData.cc = cc;
            this.setState({
                phoneCode: countryData.phoneCode,
                phoneMasks: countryData.masks,
            });
            this.phoneField.focus();
            this.props.onCountryCodeChange(e, countryData);
        }
    };

    // find the object from this.flagData array that has the same country code as the country code passed in this.props.countryCodes
    // then replace the name value for the matching object with the translation for that country code
    mergeFlagData() {
        Object.keys(this.flagData).map((countryCode) => {
            const {countryCodes} = this.props;
            // This fallback is there for react-site-footer. Remove when
            // that component is updated.
            const countryCodeTranslatedName =
                countryCodes[`reactInputTel.countries.${countryCode}`] ||
                countryCodes[countryCode];
            if (countryCodeTranslatedName) {
                this.flagData[countryCode].name = countryCodeTranslatedName;
            }
        });

        this.dropdownItems = this.buildSortedCountryItems(this.flagData);
    }

    // sort the countries alphabetically by name
    buildSortedCountryItems(flagData) {
        return Object.keys(flagData)
            .sort((a, b) => {
                // Since object keys are unique, there's no possibility of the keys
                // being equal. If a is not less than it is greater than b.
                return flagData[a].name
                    .toUpperCase()
                    .localeCompare(flagData[b].name.toUpperCase());
            })
            .map((cc) => {
                const data = flagData[cc];
                return {
                    value: cc,
                    label: `${data.name} (+${data.phoneCode})`,
                };
            });
    }

    setPhoneFieldRef = (ref) => {
        this.phoneField = ref;
    };

    render() {
        const {
            id,
            phoneNumber,
            l10n,
            errorMsg,
            helpText,
            onPhoneChange,
            onPhoneFocus,
            onPhoneBlur,
            showCountrySelect,
            useMask,
            'aria-required': ariaRequired,
        } = this.props;

        const containerClassName = classNames(this.props.className, {
            'react-input-tel': true,
            'input-group': true,
        });

        return (
            <div className={containerClassName}>
                {showCountrySelect && (
                    <FlagDropdown
                        id={`${id}__dropdown`}
                        phoneCode={this.state.phoneCode}
                        countryCode={this.props.countryCode}
                        items={this.dropdownItems}
                        onChange={this.handleCountryCodeChange}
                        l10n={l10n}
                    />
                )}
                {useMask && (
                    <PhoneField
                        id={`${id}__input`}
                        phoneCode={this.state.phoneCode}
                        l10n={l10n}
                        masks={this.state.phoneMasks}
                        phoneNumber={phoneNumber}
                        helpText={helpText}
                        errorMsg={errorMsg}
                        onChange={onPhoneChange}
                        onFocus={onPhoneFocus}
                        onBlur={onPhoneBlur}
                        showCountrySelect={showCountrySelect}
                        ref={this.setPhoneFieldRef}
                        aria-required={ariaRequired}
                    />
                )}
                {!useMask && (
                    <PhoneFieldWithoutMask
                        id={`${id}__input`}
                        phoneCode={this.state.phoneCode}
                        l10n={l10n}
                        phoneNumber={phoneNumber}
                        helpText={helpText}
                        errorMsg={errorMsg}
                        onChange={onPhoneChange}
                        onFocus={onPhoneFocus}
                        onBlur={onPhoneBlur}
                        showCountrySelect={showCountrySelect}
                        ref={this.setPhoneFieldRef}
                        aria-required={ariaRequired}
                    />
                )}
            </div>
        );
    }
}

export default InputTel;
